import {feinError, hasActiveOfficer, mailingAddressError, officerError, principalAddressError, registeredAgentError} from './company-review';

/** A problem the server found in the stored company data (GET /filing/reinstatement/options → reviewIssues). */
export interface ReviewIssue {
    /** A path into the options response's company: fein, principalAddress.zipCode, registeredAgent.state, officers, officers.0.title, ... */
    field: string;
    /** A complete sentence that already names what it is about ("Principal address: A valid US ZIP code is required."). */
    message: string;
}

export interface ReviewRestrictions {
    generalPartnersAddressOnly?: boolean;
    registeredAgentLocked?: boolean;
}

/**
 * The part of the review an issue belongs to, which the visitor fixes with one editor:
 * fein, principalAddress, mailingAddress, registeredAgent, officers (no officers at all), officers.<index>, or '' when unknown.
 */
export type ReviewItem = string;

export interface OutstandingReviewIssue extends ReviewIssue {
    item: ReviewItem;
    /** False when the visitor cannot fix it on this page (unknown field or a locked part); it is shown but does not block signing. */
    blocking: boolean;
}

export function reviewIssues(value: unknown): ReviewIssue[] {
    return Array.isArray(value) ? value.filter(issue => typeof issue?.message === 'string' && issue.message.trim())
        .map(issue => ({field: typeof issue.field === 'string' ? issue.field : '', message: issue.message})) : [];
}

export function reviewItem(field: string): ReviewItem {
    const [head, index] = field.split('.');
    if (['fein', 'principalAddress', 'mailingAddress', 'registeredAgent'].includes(head)) return head;
    if (head === 'officers') return index !== undefined && /^\d+$/.test(index) ? `officers.${index}` : 'officers';
    return '';
}

/** The client-side check for one review item, or undefined when the item is unknown. */
export function reviewItemError(company: any, item: ReviewItem): string | null | undefined {
    if (!company) return undefined;
    if (item === 'fein') return feinError(company.fein);
    if (item === 'principalAddress') return principalAddressError(company.principalAddress);
    if (item === 'mailingAddress') return mailingAddressError(company.mailingAddress);
    if (item === 'registeredAgent') return registeredAgentError(company.registeredAgent, company.name, {requireType: true});
    if (item === 'officers') return hasActiveOfficer(company.officers) ? null : 'Add at least one officer or principal.';
    const officer = /^officers\.(\d+)$/.exec(item);
    if (officer) {
        const current = company.officers?.[Number(officer[1])];
        return current === undefined ? undefined : officerError(current);
    }
    return undefined;
}

const itemValue = (company: any, item: ReviewItem) => {
    const officer = /^officers\.(\d+)$/.exec(item);
    return JSON.stringify(officer ? company?.officers?.[Number(officer[1])] : item === 'officers' ? company?.officers : company?.[item]);
};

/**
 * The server's issues that are still open in the visitor's current review. An issue is resolved once its item passes
 * the client-side check, and, if the client check did not flag the stored value itself, the visitor has also changed it.
 */
export function outstandingReviewIssues(issues: ReviewIssue[], initialCompany: any, currentCompany: any,
                                        restrictions: ReviewRestrictions = {}): OutstandingReviewIssue[] {
    return issues.flatMap<OutstandingReviewIssue>(issue => {
        const item = reviewItem(issue.field);
        // General partners' names and titles can't be edited during reinstatement, only their addresses.
        const partnerDetail = /^officers\.\d+\.(?!(?:address|street1|street2|city|state|zipCode|country)$)/.test(issue.field);
        const locked = (item === 'registeredAgent' && !!restrictions.registeredAgentLocked)
            || ((item === 'officers' || partnerDetail) && !!restrictions.generalPartnersAddressOnly);
        const error = item === '' ? undefined : reviewItemError(currentCompany, item);
        if (error === undefined || locked) return [{...issue, item, blocking: false}];
        const resolved = error === null && (reviewItemError(initialCompany, item) !== null
            || itemValue(initialCompany, item) !== itemValue(currentCompany, item));
        return resolved ? [] : [{...issue, item, blocking: true}];
    });
}
